import * as THREE from 'three';

/**
 * Dust.ts — a tiny pooled particle burst (landings, bulldozer hits). One
 * InstancedMesh of soft quads; spawn() recycles the oldest slots. Visual-only
 * and cheap enough for the phone budget.
 */
const POOL = 48;

interface Particle {
  life: number; // seconds remaining (<= 0 = free)
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  size: number;
}

export class Dust {
  readonly mesh: THREE.InstancedMesh;
  private parts: Particle[] = [];
  private next = 0;
  private mat = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private p = new THREE.Vector3();
  private s = new THREE.Vector3();

  constructor() {
    const geo = new THREE.IcosahedronGeometry(0.5, 0);
    const material = new THREE.MeshStandardMaterial({
      color: 0xcabd9c,
      transparent: true,
      opacity: 0.55,
      flatShading: true,
      roughness: 1,
      depthWrite: false,
    });
    this.mesh = new THREE.InstancedMesh(geo, material, POOL);
    this.mesh.frustumCulled = false;
    for (let i = 0; i < POOL; i++) {
      this.parts.push({ life: 0, x: 0, y: -999, z: 0, vx: 0, vy: 0, vz: 0, size: 0 });
    }
  }

  /** Kick up `n` puffs around (x, y, z). */
  spawn(x: number, y: number, z: number, n: number): void {
    for (let k = 0; k < n; k++) {
      const part = this.parts[this.next];
      this.next = (this.next + 1) % POOL;
      const a = (k / n) * Math.PI * 2 + this.next;
      part.life = 0.55 + (k % 3) * 0.12;
      part.x = x + Math.cos(a) * 0.8;
      part.y = y + 0.3;
      part.z = z + Math.sin(a) * 0.8;
      part.vx = Math.cos(a) * (3.2 + (k % 2) * 1.6);
      part.vy = 2.6 + (k % 3);
      part.vz = Math.sin(a) * (3.2 + (k % 2) * 1.6);
      part.size = 0.7 + (k % 3) * 0.35;
    }
  }

  update(dt: number): void {
    for (let i = 0; i < POOL; i++) {
      const part = this.parts[i];
      if (part.life > 0) {
        part.life -= dt;
        part.x += part.vx * dt;
        part.y += part.vy * dt;
        part.z += part.vz * dt;
        part.vy -= 7.5 * dt; // light gravity — puffs hang, then settle
        const grow = part.size * (1.6 - part.life);
        this.p.set(part.x, part.y, part.z);
        this.s.setScalar(Math.max(0.001, grow * Math.max(0, part.life * 1.6)));
        this.mesh.setMatrixAt(i, this.mat.compose(this.p, this.q, this.s));
      } else {
        this.p.set(0, -999, 0);
        this.s.setScalar(0.001);
        this.mesh.setMatrixAt(i, this.mat.compose(this.p, this.q, this.s));
      }
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}
